import {
  getUserDetails,
  isUserLoggedIn,
  _getUserDetails
} from '@mmt/legacy-commons/Native/UserSession/UserSessionModule';
import { getAndUpdateDvid } from 'MMT-UI/helpers/client/dvid/index.js';
import {Platform} from 'react-native'
import GenericModule from '@mmt/legacy-commons/Native/GenericModule';
import OmnitureModule from '@mmt/legacy-commons/Native/OmnitureModule';
import { isAndroid, isIos ,isApp } from './device';
import { getDataFromStorage, setDataInStorage } from '@mmt/legacy-commons/AppState/LocalStorage';
import isEmpty from 'lodash/isEmpty';
import {
  RAILS_SESSION_ID,
  RAILS_SESSION_ID_EXPIRY_IN_MINUTES,
  APP_CMP_VALUES,
  APP_DEFAULT_CMP_VALUE,
  setDataToSessionStorageWithExpiry,
  getDataFromSessionStorageWithExpiry,
} from './RailsConstant';
import { PAGE } from '../types/grafanaTracking.types';
import { apiSets } from '../Utils/grafanaTrackedApiSet';

let deviceDetails = null;
let userDetails = null;
let sessionID = '';
let deviceID = '';

const createDeviceDetails = async () => {
  if (!deviceDetails) {
    if (isIos()) {
      const deviceInfo = await OmnitureModule.deviceInfo();
      if (!isEmpty(deviceInfo)) {
        deviceDetails = {
          os: 'ios',
          osVersion: deviceInfo.dvc_os_ver,
          deviceType: deviceInfo.dvc_type,
          appVersion: deviceInfo.dvc_app_ver,
          deviceId: deviceInfo.dvc_did,
          networkType: deviceInfo.dvc_conn_type,
          deviceName: `${deviceInfo.dvc_manuf} ${deviceInfo.dvc_mdl}`,
        };
      }
    } else if (isAndroid()) {
      const deviceInfo = await GenericModule.getDeviceInfo();
      if (!isEmpty(deviceInfo)) {
        const { androidVersion, phoneNumber, accountEmailId, ...rest } = deviceInfo;
        deviceDetails = {
          ...rest,
          os: 'android',
          osVersion: deviceInfo.androidVersion,
          deviceId: deviceInfo.pdtDeviceId,
          deviceType: 'Mobile',
        };
      }
    }
  }
  return deviceDetails;
};

const getPlatformBasedUserDetails = async () => {
  if(Platform.OS === 'web'){
    return await _getUserDetails();
  }else {
    return await getUserDetails();
  }
}

export const getNativeUserDetails = async () => {
  if (!userDetails) {
    const loggedIn = await isUserLoggedIn();
    userDetails = loggedIn ? await getPlatformBasedUserDetails() : null;
  }
  return userDetails;
};

export const getDeviceDetails = createDeviceDetails;

export const getQueryParamsFromDeepLink = (url) => {
  const urlSplits = decodeURIComponent(url).split('?');
  const pairs = urlSplits[2].split('&');
  const result = {};

  pairs.forEach((pair) => {
    const keyValues = pair.split('=');
    result[keyValues[0]] = decodeURIComponent(keyValues[1] || '');
  });

  return result;
};

const randomString = () =>
  Math.floor((1 + Math.random()) * 0x10000)
    .toString(16)
    .substring(1);

const generateRandomID = () => {
  const id = `${
    randomString() + randomString()
  }-${randomString()}-${randomString()}-${randomString()}-${
    randomString() + randomString() + randomString()
  }`;
  return id;
};

const generateNewSessionID = async () => {
  const id = deviceID ?? generateRandomID();
  const sessionID = `${id}_${Platform.OS}_${Date.now()}_${randomString()}`;
  await setDataToSessionStorageWithExpiry(RAILS_SESSION_ID, sessionID);
  return sessionID;
};

const getCurrentSessionIDFromStorage = async () => {
  const id = await getDataFromSessionStorageWithExpiry(RAILS_SESSION_ID);
  return id ?? '';
};

export const syncSessionID = async () => {
  if (isEmpty(deviceID)) {
    deviceID = getAndUpdateDvid();
  }
  if (isEmpty(sessionID)) sessionID = await getCurrentSessionIDFromStorage();
};

export const resetSessionID = async () => {
  sessionID = await generateNewSessionID();
};

export const getSessionID = async () => {
  try {
    const storedSessionID = await getCurrentSessionIDFromStorage();
  if (storedSessionID) {
      sessionID = storedSessionID;
      return sessionID;
    }
    sessionID = await generateNewSessionID();
    return sessionID;
  } catch (err) {
    console.log('Error in getting session ID: ', err);
    sessionID = await generateNewSessionID();
    return sessionID;
  }
};


const findApiInSet = (url, apiSet, pageName) => {
  for (const currentApi of apiSet) {
    const { api, name } = currentApi;
    if (url.includes(api)) {
      return { pageName, entityName: name };
    }
  }
  return null;
};

export const getPageNameAndEntityNameOfApi = (url) => {
  for (const [pageName, apiSet] of Object.entries(apiSets)) {
    const result = findApiInSet(url, apiSet, PAGE[pageName]);
    if (result) {
      return result;
    }
  }

  return { pageName: '', entityName: '' };
};

export const getSessionDetails = async () => {
  try {
    if (GenericModule?.getSessionInfo) {
      const deepLinkTrackingData = await GenericModule.getSessionInfo();
      return deepLinkTrackingData;
    }
    return null;
  } catch (err) {
    return null;
  }
};

export const setCookie = (cookieName, cookieValue, expiresInMonths = 12) => {
  if (isApp()) return;
  const myDate = new Date();
  myDate.setMonth(myDate.getMonth() + expiresInMonths);
  document.cookie = cookieName + "=" + cookieValue + ";expires=" + myDate
    + ";domain=.makemytrip.com;path=/";
}

export const getCookieValue = (name) => {
  if (isApp()) return '';
  return document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || '';
}

export const isClientRender = () => typeof window === "object";

const hostname = () => isClientRender() ? window.location.hostname : "";

export const getPwaServerUrl = () => {
  if(Platform.OS === 'web'){
    if (isClientRender() && (hostname() === "rails.makemytrip.com" || hostname() === "localhost")) {
      return "/";
    } else {
      return "https://rails.makemytrip.com/"
    }
  } else {
    return "https://rails.makemytrip.com/"
  }
}

export const isObject = (value) => {
  return typeof value === 'object' &&
    !Array.isArray(value) &&
    value !== null;
}

export const getAppLaunchSource = async () => {
  let launchSource = APP_DEFAULT_CMP_VALUE;
  try {
    const sessionDetails = await getSessionDetails();
    if (sessionDetails?.sessionCmp?.length > 0) {
      for (let i = 0; i < APP_CMP_VALUES.length; i++) {
        if (sessionDetails.sessionCmp?.startsWith(APP_CMP_VALUES[i])) {
          launchSource = APP_CMP_VALUES[i];
          break;
        }
      }
    }
    return launchSource;
  } catch (err) {
    return APP_DEFAULT_CMP_VALUE;
  }
};
